import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AdminDashboard } from '@/components/projector-watch/AdminDashboard';
import { AdminUsers } from '@/components/projector-watch/AdminUsers';
import { AdminSettings } from '@/components/projector-watch/AdminSettings';
import { BackButton } from '@/components/ui/back-button';
import { Shield, LayoutDashboard, Users, Settings, Loader2 } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

const SUPER_ADMIN_EMAIL = 'solutions.oko@gmail.com';

export default function ProjectorWatchAdminDashboard() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        if (!currentUser?.email) {
          navigate('/login');
          return;
        }

        const isAdmin = currentUser.email === SUPER_ADMIN_EMAIL;
        if (!isAdmin) {
          toast({
            variant: "destructive",
            title: "Accès refusé",
            description: "Vous n'avez pas les droits d'accès à cette section.",
          });
          navigate('/projector-watch');
          return;
        }

        setLoading(false);
      } catch (error) {
        console.error('Error checking admin access:', error);
        toast({
          variant: "destructive",
          title: "Erreur",
          description: "Impossible de vérifier les droits d'accès.",
        });
        navigate('/projector-watch');
      }
    };

    checkAdminAccess();
  }, [currentUser, navigate, toast]);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      <div className="max-w-6xl mx-auto space-y-6">
        <BackButton to="/projector-watch" />

        <Card className="card-enhanced">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-blue-600" />
              <h2 className="text-2xl font-bold">Administration Projector Watch</h2>
            </div>
            <p className="text-gray-600">
              Gérez les utilisateurs et les paramètres de l'application
            </p>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="dashboard" className="space-y-6">
              <TabsList className="grid grid-cols-3 gap-4">
                <TabsTrigger value="dashboard" className="flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />
                  Tableau de bord
                </TabsTrigger>
                <TabsTrigger value="users" className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Utilisateurs
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Paramètres
                </TabsTrigger>
              </TabsList>

              <TabsContent value="dashboard">
                <AdminDashboard />
              </TabsContent>

              <TabsContent value="users">
                <AdminUsers />
              </TabsContent>

              <TabsContent value="settings">
                <AdminSettings />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}